import { Injectable } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { BehaviorSubject, Observable, Subject, map } from 'rxjs';
import {
  FormlyFieldConfigForConverter,
  FormlyFields,
  IFormBuilderFieldsData,
} from '../models/form-builder-fields-data.model';
import {
  IBasicFieldLabelAndDetails,
  IBasicLabelValuePair,
} from '../models/basic-label-value-pair.model';
import { TFormlyFieldKey } from '../models/formly-key.type';
import { NonDataElementFormlyFieldTypes } from '../models/irembo-formly-non-data-field-types.enum';
import { EIremboFormlyFieldTypes } from '@irembo-andela/irembogov3-common';
import { ServiceChangeRequestService } from './service-change-request.service';

@Injectable({
  providedIn: 'root',
})
export class FormBuilderService {
  constructor(
    private serviceChangeRequestService: ServiceChangeRequestService
  ) {}

  deleteFieldAction$ = new Subject();
  duplicateFieldAction$ = new Subject();
  formlyFieldsSubject$: BehaviorSubject<IFormBuilderFieldsData> =
    new BehaviorSubject<IFormBuilderFieldsData>({
      fields: [],
    });
  reviewModeEnabled$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );

  configureFieldAction$ = new BehaviorSubject<FormlyFieldConfig | null>(null);

  formFieldBlockIdsSet$ = new BehaviorSubject<Set<string>>(new Set());

  removeItemAction$ = new Subject<number>();

  integrationPoint = new BehaviorSubject<any>([]);

  thirdPartyFormlyFieldsSubject$: BehaviorSubject<any> = new BehaviorSubject<
    FormlyFieldConfig[]
  >([]);

  public formUpdated$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  public reviewFlagItemSubject$: BehaviorSubject<string> =
    new BehaviorSubject<string>('');
  public formlyFieldsSaveRequested$ = new Subject<void>();

  getEndPointByCode(code: string): Observable<any> {
    return this.serviceChangeRequestService.getIntegrationEndpoint(code).pipe(
      map((res: any) => {
        return {
          url: res.data.url,
          options: this.extractUrlAttributes(res.data.customConfigJson),
        };
      })
    );
  }

  extractUrlAttributes(attributes: any) {
    const options: IBasicLabelValuePair<Record<string, unknown>>[] = [];
    const parsedJson = JSON.parse(attributes);
    Object.keys(parsedJson).forEach(function (key) {
      options.push({
        label: key,
        value: parsedJson[key],
      });
    });
    return options;
  }

  loadJsonFormDataForPreview(jsonFormData: any): void {
    this.formFieldBlockIdsSet$.next(new Set());
    if (jsonFormData?.['fields']) {
      const fields: FormlyFieldConfig[] = jsonFormData['fields'];
      this.formlyFieldsSubject$.next({ fields });
      return;
    }

    this.formlyFieldsSubject$.next({ fields: [] });
  }

  refreshFormlyForm(clearConfiguredItem = true, forceReload = true): void {
    const fields: FormlyFieldConfig[] =
      this.formlyFieldsSubject$.getValue().fields;
    this.formlyFieldsSubject$.next({ fields: [...fields], forceReload });
    if (clearConfiguredItem) {
      this.configureFieldAction$.next(null);
    }
  }

  requestFormlyFieldsSave(): void {
    this.formlyFieldsSaveRequested$.next();
  }

  /**
   * Returns true if `label` would collide with another field's label, mirroring the
   * backend check (checkFormInputLabelsIsUnique): case-insensitive, across every
   * field under sections > blocks, form-wide. Fast: a single early-exit walk of
   * exactly those three levels (no deep recursion), skipping the field being edited.
   */
  isDuplicateFieldLabel(label: string, ownKey?: string): boolean {
    const target = (label ?? '').toLowerCase();
    if (!target) return false;
    const fields: FormlyFieldConfig[] =
      this.formlyFieldsSubject$.getValue().fields || [];
    for (const wrapper of fields) {
      for (const section of wrapper.fieldGroup || []) {
        if (this.sectionHasFieldLabel(section, target, ownKey)) return true;
      }
    }
    return false;
  }

  /** Inner blocks > fields walk for a single section; early-exits on the first match. */
  private sectionHasFieldLabel(
    section: FormlyFieldConfig,
    target: string,
    ownKey?: string
  ): boolean {
    for (const block of section.fieldGroup || []) {
      for (const f of block.fieldGroup || []) {
        if (ownKey && f?.key?.toString() === ownKey) continue;
        const raw = f?.props?.['label'];
        if (typeof raw === 'string' && raw.toLowerCase() === target)
          return true;
      }
    }
    return false;
  }

  addToFormFieldBlockIdsSet(id: string) {
    const newSet: Set<string> = this.formFieldBlockIdsSet$.getValue().add(id);
    this.formFieldBlockIdsSet$.next(newSet);
  }

  removeFormFieldBlockIdsSet(id: string) {
    const existingSet: Set<string> = this.formFieldBlockIdsSet$.getValue();
    existingSet.delete(id);
    this.formFieldBlockIdsSet$.next(existingSet);
  }

  deleteFormField(fieldToDelete: FormlyFieldConfig) {
    const formlyFields = this.formlyFieldsSubject$.getValue();
    const fields = formlyFields?.fields;
    if (!fields || !fieldToDelete?.parent?.fieldGroup) {
      return;
    }

    const parentFieldGroup = fieldToDelete.parent.fieldGroup;
    if (fieldToDelete.parent.type === 'formly-group') {
      const parentFieldGroupArray = fieldToDelete.parent.fieldGroup;
      const repeaterIndex = parentFieldGroupArray.findIndex(
        f => f === fieldToDelete
      );

      if (repeaterIndex !== -1) {
        parentFieldGroupArray.splice(repeaterIndex, 1);
        const modelArray = fieldToDelete.parent.model;
        if (Array.isArray(modelArray)) {
          modelArray.splice(repeaterIndex, 1);
        }
        fieldToDelete.parent.fieldGroup = [...parentFieldGroupArray];
        const repeaterParent = fieldToDelete.parent.parent;
        const fieldArrayConfig = repeaterParent?.fieldArray;
        if (!fieldArrayConfig || typeof fieldArrayConfig === 'function') {
          return;
        }
        if (fieldArrayConfig.fieldGroup) {
          const templateIndex = fieldArrayConfig.fieldGroup.findIndex(
            f => f.key === fieldToDelete.key
          );
          if (templateIndex !== -1) {
            fieldArrayConfig.fieldGroup.splice(templateIndex, 1);
            fieldArrayConfig.fieldGroup = [...fieldArrayConfig.fieldGroup];
          }
        }
        const formlyFields = this.formlyFieldsSubject$.getValue();
        this.formlyFieldsSubject$.next({ ...formlyFields, forceReload: true });
        this.configureFieldAction$.next(null);
        return;
      }
    }
    if (!parentFieldGroup) {
      return;
    }

    switch (fieldToDelete.type) {
      case EIremboFormlyFieldTypes.block: {
        const parentBlock = fieldToDelete.parent.fieldGroup ?? [];
        const index = parentBlock.findIndex(
          field => field.id === fieldToDelete.id
        );
        if (index !== -1) {
          parentBlock.splice(index, 1);
          fieldToDelete.parent.fieldGroup = [...parentBlock];
        }
        break;
      }
      case EIremboFormlyFieldTypes.sections: {
        const sections = fields[0]?.fieldGroup ?? [];
        const sectionIndex = sections.findIndex(
          section => section.key === fieldToDelete.key
        );
        if (sectionIndex !== -1) {
          sections.splice(sectionIndex, 1);
          fields[0].fieldGroup = [...sections];
        }
        break;
      }
      default: {
        const fieldIndex = parentFieldGroup.findIndex(
          field => field.key === fieldToDelete.key
        );
        if (fieldIndex !== -1) {
          parentFieldGroup.splice(fieldIndex, 1);
          fieldToDelete.parent.fieldGroup = [...parentFieldGroup];
        }
        break;
      }
    }
    this.formlyFieldsSubject$.next({ ...formlyFields, forceReload: true });
    this.configureFieldAction$.next(null);
  }

  duplicateFormField(fieldToDuplicate: FormlyFieldConfig) {
    const formlyFields = this.formlyFieldsSubject$.getValue();
    const fields = formlyFields?.fields;
    if (!fields || !fieldToDuplicate?.parent?.fieldGroup) {
      return;
    }

    const parentFieldGroup = fieldToDuplicate.parent.fieldGroup;
    if (!parentFieldGroup) {
      return;
    }

    // Create a deep copy of the field
    const duplicateField = JSON.parse(JSON.stringify(fieldToDuplicate));

    // Generate a new unique key
    const baseKey = duplicateField.key?.toString().replace(/[0-9]+$/, '');
    const newKey = `${baseKey}${this.generateUniqueId()}`;

    // Generate a new unique id
    const baseId = duplicateField.id?.replace(/[0-9]+$/, '');
    const newId = `${baseId}${this.generateUniqueId()}`;

    // Update the IDs and keys
    duplicateField.key = newKey;
    duplicateField.id = newId;

    // Reset any instance-specific properties if needed
    duplicateField.parent = fieldToDuplicate.parent;
    duplicateField.defaultValue = null;

    // Deep copy any expressions to prevent shared references
    if (duplicateField.expressions) {
      duplicateField.expressions = JSON.parse(
        JSON.stringify(duplicateField.expressions)
      );
    }

    // Deep copy validators to prevent shared references
    if (duplicateField.validators) {
      duplicateField.validators = JSON.parse(
        JSON.stringify(duplicateField.validators)
      );
    }

    // Deep copy props to prevent shared references
    if (duplicateField.props) {
      duplicateField.props = JSON.parse(JSON.stringify(duplicateField.props));
    }

    // Update the label to indicate it's a copy and prevent duplicate labels
    if (duplicateField.props?.label) {
      const originalLabel = fieldToDuplicate.props?.label ?? '';
      // Count existing copies in the parent to determine the copy number
      const siblingLabels = parentFieldGroup
        .map(f => f.props?.label ?? '')
        .filter(
          label =>
            label === originalLabel ||
            label.match(
              new RegExp(
                `^${originalLabel.replace(
                  /[.*+?^${}()|[\]\\]/g,
                  '\\$&'
                )} \\(Copy \\d+\\)$`
              )
            )
        );
      const copyNumber = siblingLabels.length;
      duplicateField.props.label = `${originalLabel} (Copy ${copyNumber})`;
    }

    switch (fieldToDuplicate.type) {
      case EIremboFormlyFieldTypes.block: {
        const parentBlock = fieldToDuplicate.parent.fieldGroup ?? [];
        const index = parentBlock.findIndex(
          field => field.id === fieldToDuplicate.id
        );
        if (index !== -1) {
          parentBlock.splice(index + 1, 0, duplicateField);
          fieldToDuplicate.parent.fieldGroup = [...parentBlock];
        }
        break;
      }
      case EIremboFormlyFieldTypes.sections: {
        const sections = fields[0]?.fieldGroup ?? [];
        const sectionIndex = sections.findIndex(
          section => section.key === fieldToDuplicate.key
        );
        if (sectionIndex !== -1) {
          sections.splice(sectionIndex + 1, 0, duplicateField);
          fields[0].fieldGroup = [...sections];
        }
        break;
      }
      default: {
        const fieldIndex = parentFieldGroup.findIndex(
          field => field.key === fieldToDuplicate.key
        );
        if (fieldIndex !== -1) {
          parentFieldGroup.splice(fieldIndex + 1, 0, duplicateField);
          fieldToDuplicate.parent.fieldGroup = [...parentFieldGroup];
        }
        break;
      }
    }

    this.formlyFieldsSubject$.next({ ...formlyFields, forceReload: true });
    this.configureFieldAction$.next({
      ...duplicateField,
      parent: fieldToDuplicate.parent,
    });
  }

  private generateUniqueId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
  }

  startOnlyFormPreviewSubjects() {
    this.initFormlyFieldsSubject();
    this.initFormFieldBlockIdsSetSubject();
    this.initreviewModeEnabledSubject();
  }

  completeOnlyFormPreviewSubjects() {
    this.formlyFieldsSubject$.complete();
    this.formFieldBlockIdsSet$.complete();
    this.reviewModeEnabled$.complete();
  }

  startFormBuilderConfigurationSubjects() {
    this.startOnlyFormPreviewSubjects();
    this.initConfigureFieldActionSubject();
  }

  completeFormBuilderConfigurationSubjects() {
    this.completeOnlyFormPreviewSubjects();
    this.configureFieldAction$.complete();
  }

  findFormlyFieldConfigInFormByKey(key: string): FormlyFieldConfig | undefined {
    return this.getFormFieldOptionLabelAndValue({
      compareField: {
        value: key,
      },
    });
  }

  getFormFieldOptionLabelAndValue(model: any): FormlyFieldConfig {
    let optionsField!: FormlyFieldConfig;
    const fields = this.formlyFieldsSubject$.getValue().fields;
    return this.formFieldOptionsExtractor(
      model?.compareField?.value,
      optionsField,
      fields
    );
  }

  getAllFormFieldLabelAndDetails(
    dataEntryFieldsOnly = false,
    removeConfiguredKey = false,
    excludedFieldTypes: EIremboFormlyFieldTypes[] = [],
    excludeNonConfigurableFields = true
  ): IBasicFieldLabelAndDetails<TFormlyFieldKey>[] {
    const fields: FormlyFieldConfig[] =
      this.formlyFieldsSubject$.getValue().fields;
    let formFieldLabelAndDetails: IBasicFieldLabelAndDetails<TFormlyFieldKey>[] =
      [];
    formFieldLabelAndDetails = this.formFieldLabelAndDetailsBuilder(
      fields,
      formFieldLabelAndDetails,
      dataEntryFieldsOnly,
      removeConfiguredKey,
      excludedFieldTypes,
      excludeNonConfigurableFields
    );
    return formFieldLabelAndDetails;
  }

  getCurrentConfiguredFieldLabelAndDetails():
    | undefined
    | IBasicFieldLabelAndDetails<TFormlyFieldKey> {
    if (!this.configureFieldAction$.getValue()) {
      return undefined;
    }
    return this.dataEntryFieldsAllowedFieldExtractor(
      <FormlyFieldConfig>this.configureFieldAction$.getValue(),
      []
    )?.[0];
  }

  getConfiguredFieldSiblingsFieldLabelAndDetails(
    dataEntryFieldsOnly = false,
    removeConfiguredKey = false,
    excludeNonConfigurableFields = true
  ): IBasicFieldLabelAndDetails<TFormlyFieldKey>[] {
    const siblingFields: FormlyFieldConfig[] =
      this.configureFieldAction$.getValue()?.parent?.fieldGroup || [];
    let siblingFieldsFieldLabelAndDetails: IBasicFieldLabelAndDetails<TFormlyFieldKey>[] =
      [];
    siblingFieldsFieldLabelAndDetails = this.formFieldLabelAndDetailsBuilder(
      siblingFields,
      siblingFieldsFieldLabelAndDetails,
      dataEntryFieldsOnly,
      removeConfiguredKey,
      [],
      excludeNonConfigurableFields
    );
    return siblingFieldsFieldLabelAndDetails;
  }

  private formFieldOptionsExtractor(
    fieldKey: string,
    optionsField: FormlyFieldConfig,
    fields: FormlyFieldConfig[]
  ): FormlyFieldConfig {
    const formFieldWithOptions = this.formFieldsExtractor(fields, []);
    const formlyFieldConfig: any = formFieldWithOptions.find(
      field => field.key == fieldKey
    );
    return formlyFieldConfig;
  }

  formFieldsExtractor(
    fields: FormlyFieldConfig[],
    resultFields: FormlyFieldConfig[]
  ): FormlyFieldConfig[] {
    const fieldsWithOptions: string[] = [
      EIremboFormlyFieldTypes.customdropdown,
      EIremboFormlyFieldTypes.radio,
      EIremboFormlyFieldTypes.checkbox,
      EIremboFormlyFieldTypes.multicheckbox,
      EIremboFormlyFieldTypes.customdropdownpaginated,
    ];
    fields.forEach(field => {
      if (field.fieldGroup && field.fieldGroup.length > 0) {
        this.formFieldsExtractor(field.fieldGroup, resultFields);
      } else {
        const fieldType = field.type;
        if (fieldsWithOptions.includes(<string>fieldType)) {
          resultFields.push(field);
        }
      }
    });
    return resultFields;
  }

  private formFieldLabelAndDetailsBuilder(
    fields: FormlyFieldConfig[],
    formFieldLabelAndDetails: IBasicFieldLabelAndDetails<TFormlyFieldKey>[],
    dataEntryFieldsOnly: boolean,
    removeConfiguredKey: boolean,
    excludedFieldTypes: EIremboFormlyFieldTypes[] = [],
    excludeNonConfigurableFields = true
  ): IBasicFieldLabelAndDetails<TFormlyFieldKey>[] {
    fields.forEach(field => {
      const checksPassed: boolean = this.dataEntryFieldsAllowedFieldChecks(
        field,
        dataEntryFieldsOnly,
        removeConfiguredKey,
        excludedFieldTypes,
        excludeNonConfigurableFields
      );

      if (checksPassed) {
        formFieldLabelAndDetails = this.dataEntryFieldsAllowedFieldExtractor(
          field,
          formFieldLabelAndDetails
        );
      }

      if (field.fieldGroup) {
        this.formFieldLabelAndDetailsBuilder(
          field.fieldGroup,
          formFieldLabelAndDetails,
          dataEntryFieldsOnly,
          removeConfiguredKey,
          excludedFieldTypes,
          excludeNonConfigurableFields
        );
      }
    });

    return formFieldLabelAndDetails;
  }

  private dataEntryFieldsAllowedFieldChecks(
    field: FormlyFieldConfig,
    dataEntryFieldsOnly: boolean,
    removeConfiguredKey: boolean,
    excludedFieldTypes: EIremboFormlyFieldTypes[],
    excludeNonConfigurableFields = true
  ): boolean {
    if (
      dataEntryFieldsOnly &&
      NonDataElementFormlyFieldTypes.indexOf(
        field.type as EIremboFormlyFieldTypes
      ) >= 0
    ) {
      return false;
    }

    if (excludeNonConfigurableFields && field.props?.['nonConfigurable']) {
      return false;
    }

    if (
      removeConfiguredKey &&
      field.key === this.configureFieldAction$.getValue()?.key
    ) {
      return false;
    }
    if (
      field.type &&
      excludedFieldTypes.includes(<EIremboFormlyFieldTypes>field.type)
    ) {
      return false;
    }

    return true;
  }

  private dataEntryFieldsAllowedFieldExtractor(
    field: FormlyFieldConfig,
    formFieldLabelAndDetails: IBasicFieldLabelAndDetails<TFormlyFieldKey>[]
  ): IBasicFieldLabelAndDetails<TFormlyFieldKey>[] {
    if (field.key && field?.props?.label) {
      const stringPathArray: string[] = [];
      this.getFieldRootRef(field, stringPathArray);

      const rootModelPath = `${Array(stringPathArray.length + 1)
        .fill('parent?')
        .join('.')}`;
      const stringPathArrayReversed: string[] = [...stringPathArray].reverse();
      const fieldRefPath: string = stringPathArrayReversed.join('?.');

      formFieldLabelAndDetails.push({
        label: field?.props.label,
        value: field?.key,
        fieldKey: field?.key ? <string>field?.key : undefined,
        fieldType: field.type ? <string>field.type : undefined,
        propsType: field?.props?.type,
        rootModelPath: `field?.${rootModelPath}.model`,
        fieldRefPath: fieldRefPath ? `${fieldRefPath}` : undefined,
        fieldSubType: field.props?.['subType'] ?? undefined,
      });
    }
    return formFieldLabelAndDetails;
  }

  private getFieldRootRef(
    field: FormlyFieldConfig,
    pathArray: string[]
  ): string[] {
    if (field?.key) {
      pathArray.push(<string>field.key);
    }
    if (field?.parent?.key) {
      this.getFieldRootRef(field.parent, pathArray);
    }
    return pathArray;
  }

  private initFormlyFieldsSubject() {
    this.formlyFieldsSubject$ = new BehaviorSubject<IFormBuilderFieldsData>({
      fields: [],
    });
  }

  private initConfigureFieldActionSubject() {
    this.configureFieldAction$ = new BehaviorSubject<FormlyFieldConfig | null>(
      null
    );
  }

  private initFormFieldBlockIdsSetSubject() {
    this.formFieldBlockIdsSet$ = new BehaviorSubject<Set<string>>(new Set());
  }

  private initreviewModeEnabledSubject() {
    this.reviewModeEnabled$ = new BehaviorSubject<boolean>(false);
  }

  arrayToJsonObject = (
    arr: [string, { fields: FormlyFieldConfigForConverter[] }][]
  ): FormlyFields => {
    return arr.reduce((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {} as FormlyFields);
  };
}
